import { MouseEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Flex, forwardRef, InformationCircleOutline, Text, Tooltip, VStack } from '@bonusly/design-system';
import { useCurrentCompany } from '@bonusly/who-am-i';
import { Modifier } from '@popperjs/core';

import { SelectorOption } from '../../components/selector/selector-option';
import { SelectorBox, SelectorProps } from '../selector/selector-box';
import { useSelector } from '../selector/use-selector';

const normalizeHashtag = (hashtag: string) => hashtag.replace(/^#/, '').toLowerCase();

const tooltipZIndexModifier: Partial<Modifier<'tooltipZIndex', object>> = {
	name: 'tooltipZIndex',
	enabled: true,
	phase: 'write',
	fn: ({ state }) => {
		state.elements.popper.style.zIndex = '10000';
	},
};

const preventOptionSelection = (event: MouseEvent<HTMLSpanElement>) => {
	event.preventDefault();
	event.stopPropagation();
};

const shouldShowCoreValueInfo = () => {
	if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
		return true;
	}

	// Match the "sm" breakpoint behavior and hide info affordances on small/mobile viewports.
	return !window.matchMedia('(max-width: 30em)').matches;
};

export const HashtagSelector = forwardRef<SelectorProps<string>, 'div'>(({ command, hash, items, query }, ref) => {
	const { t } = useTranslation('components');
	const company = useCurrentCompany();
	const showCoreValueInfo = shouldShowCoreValueInfo();
	const { getOptionProps, getSelectorProps, selectedIndex } = useSelector({
		command,
		hash,
		items,
		ref,
	});
	const coreValueDescriptions = useMemo(
		() =>
			new Map((company.core_values ?? []).map((coreValue) => [normalizeHashtag(coreValue.hashtag), coreValue.description] as const)),
		[company.core_values]
	);

	if (!items.length) {
		return null;
	}

	return (
		<SelectorBox>
			<VStack spacing={0} align="stretch" {...getSelectorProps()}>
				{!query && (
					<Box p={2} color="text.softer" fontSize="sm" fontWeight="medium" lineHeight={5}>
						{t('rich_media.search_hashtags.help_text')}
					</Box>
				)}

				{!items.length && (
					<Box p={2} color="text.softer" fontSize="sm" fontWeight="medium" lineHeight={5}>
						{t('rich_media.no_results')}
					</Box>
				)}

				{items.map((hashtag, index) => {
					const key = `${hashtag}-${index}`;
					const description = coreValueDescriptions.get(normalizeHashtag(hashtag));

					return (
						<SelectorOption isSelected={selectedIndex === index} key={key} textAlign="left" {...getOptionProps(index)}>
							<Flex alignItems="center" justifyContent="space-between" gap={2}>
								<Text fontSize="md" fontWeight="medium" lineHeight={6}>
									#{hashtag}
								</Text>
								{description && showCoreValueInfo && (
									<Tooltip label={description} placement="top" modifiers={[tooltipZIndexModifier]}>
										<Box
											as="span"
											data-testid={`core-value-info-${hashtag}`}
											color="text.softest"
											display="inline-flex"
											alignItems="center"
											cursor="help"
											onMouseDown={preventOptionSelection}
											onClick={preventOptionSelection}
											_hover={{ color: 'text.default' }}
										>
											<InformationCircleOutline size="sm" />
										</Box>
									</Tooltip>
								)}
							</Flex>
						</SelectorOption>
					);
				})}
			</VStack>
		</SelectorBox>
	);
});
